/**
 * Transaction Item Component
 * 
 * Displays individual transaction in the list
 */

import React, { useState, useEffect, useRef } from 'react';
import { Transaction } from '../../types/transaction.types';
import type { Account } from '../../types/account.types';
import type { Category } from '../../types/category.types';
import { useUpdateTransaction } from '../../hooks/useTransactions';
import { transactionTagsService } from '../../services/transaction-tags-service';
import { getCurrentUserId } from '../../lib/supabase';
import { ArrowDown, ArrowUp, ArrowLeftRight, CheckCircle, Clock, AlertCircle, Tag as TagIcon, Scissors, ChevronUp, ChevronDown, X, Save, ExternalLink, FolderTree, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { transactionKeys } from '../../hooks/useTransactions';
import { getCategoryIconComponent } from '../../utils/icon-utils';
import { usePreferences } from '../../hooks/usePreferences';
import type { UserPreferences } from '../../types/preference.types';
import { formatTransactionAmount } from '../../utils/currency-formatter';
import { useRegion } from '../../contexts/RegionContext';

interface TransactionItemProps {
  transaction: Transaction;
  account?: Account; // Account information for this transaction
  category?: Category; // Category information for this transaction
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
  onEdit: () => void;
  onCategorise: () => void; // Added
  onSplit: () => void;
  onTag: () => void;
  onDelete: () => void;
  viewMode?: 'card' | 'table'; // View mode: 'card' for mobile, 'table' for desktop
}

export function TransactionItem({
  transaction,
  account,
  category,
  isSelected,
  onSelect,
  onEdit: _onEdit,
  onCategorise, // Added
  onSplit,
  onTag,
  onDelete,
  viewMode = 'card', // Default to card view
}: TransactionItemProps): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const [description, setDescription] = useState(transaction.description || '');
  const [merchantName, setMerchantName] = useState(transaction.merchantName || '');
  const [type, setType] = useState<'debit' | 'credit' | 'transfer'>(transaction.type);
  const [status, setStatus] = useState<'completed' | 'pending' | 'needs_review'>(transaction.status || 'pending');
  const [notes, setNotes] = useState(transaction.notes || '');
  const [tags, setTags] = useState<Array<{ id: string; name: string; color: string }>>(transaction.tags || []);
  const [loadingTags, setLoadingTags] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // Store original values when accordion opens to compare against
  const [originalValues, setOriginalValues] = useState<{
    description: string;
    merchantName: string;
    type: 'debit' | 'credit' | 'transfer';
    status: 'completed' | 'pending' | 'needs_review';
    notes: string;
  } | null>(null);
  
  // Track if we just saved to prevent syncing with stale transaction prop
  const justSavedRef = useRef(false);
  // Track timeout ID to clear it if needed
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track the last saved values to compare against transaction prop
  const lastSavedValuesRef = useRef<{
    description: string;
    merchantName: string;
    type: 'debit' | 'credit' | 'transfer';
    status: 'completed' | 'pending' | 'needs_review';
    notes: string;
  } | null>(null);

  const updateTransactionMutation = useUpdateTransaction();
  const queryClient = useQueryClient();
  const { data: preferences } = usePreferences();
  const { formatDate } = useRegion();

  const formatAmount = (amount: number, type: 'debit' | 'credit' | 'transfer') => {
    // Use original currency from transaction if available (for existing transactions)
    const originalCurrency = (transaction as any).originalCurrency;
    return formatTransactionAmount(amount, type, (preferences ?? undefined) as UserPreferences | undefined, originalCurrency);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'debit':
        return <ArrowDown className="h-4 w-4 text-red-600 dark:text-red-500" />;
      case 'credit':
        return <ArrowUp className="h-4 w-4 text-green-600 dark:text-green-500" />;
      case 'transfer':
        return <ArrowLeftRight className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
      default:
        return null;
    }
  };

  const getTypeIconBackground = (type: string): string => {
    switch (type) {
      case 'credit':
        // Green background for credit (matching green-600)
        return 'rgba(22, 163, 74, 0.12)'; // green-600 with 12% opacity
      case 'debit':
        // Pale red background for debit (matching red-600)
        return 'rgba(220, 38, 38, 0.12)'; // red-600 with 12% opacity
      case 'transfer':
        return 'rgba(14, 165, 233, 0.12)'; // blue-500 with 12% opacity
      default:
        return 'rgba(156, 163, 175, 0.12)'; // gray-400 with 12% opacity
    }
  };

  // @ts-expect-error - Intentionally unused, kept for future use
  const _getStatusIcon = (_status: string) => {
    switch (_status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />;
      case 'needs_review':
        return <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  // @ts-expect-error - Intentionally unused, kept for future use
  const _getStatusLabel = (_status: string) => {
    switch (status) {
      case 'completed':
        return 'Categorised';
      case 'pending':
        return 'Uncategorised';
      case 'needs_review':
        return 'Needs Review';
      default:
        return 'Unknown';
    }
  };

  const DEFAULT_CATEGORY_COLOR = '#3B82F6';

  const hexToRgba = (hex: string | undefined, alpha: number): string => {
    if (!hex) return `rgba(59, 130, 246, ${alpha})`;
    let sanitized = hex.replace('#', '');
    if (sanitized.length === 3) {
      sanitized = sanitized
        .split('')
        .map((char) => char + char)
        .join('');
    }
    if (sanitized.length !== 6) {
      return `rgba(59, 130, 246, ${alpha})`;
    }
    const r = Number.parseInt(sanitized.slice(0, 2), 16);
    const g = Number.parseInt(sanitized.slice(2, 4), 16);
    const b = Number.parseInt(sanitized.slice(4, 6), 16);
    if ([r, g, b].some((val) => Number.isNaN(val))) {
      return `rgba(59, 130, 246, ${alpha})`;
    }
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getAccentColor = (
    category?: Category,
    transactionType?: 'debit' | 'credit' | 'transfer'
  ): string => {
    if (category?.color) {
      return category.color;
    }
    switch (transactionType) {
      case 'debit':
        return '#EF4444';
      case 'credit':
        return '#22C55E';
      case 'transfer':
        return '#0EA5E9';
      default:
        return DEFAULT_CATEGORY_COLOR;
    }
  };

  // @ts-expect-error - Intentionally unused, kept for future use
  const _handleKeyDown = (_e: React.KeyboardEvent, _action: () => void) => {
    // Intentionally empty
  };

  // Fetch tags when expanded
  useEffect(() => {
    if (isExpanded && tags.length === 0) {
      const loadTags = async () => {
        setLoadingTags(true);
        try {
          const userId = await getCurrentUserId();
          if (!userId) return;
          
          const result = await transactionTagsService.getTagsForTransaction(transaction.id, userId);
          if (result.data) {
            setTags(result.data);
          }
        } catch (error) {
          console.error('Failed to load tags:', error);
        } finally {
          setLoadingTags(false);
        }
      };
      loadTags();
    }
  }, [isExpanded, transaction.id, tags.length]);

  // Initialize state when component mounts or transaction ID changes (new transaction selected)
  useEffect(() => {
    setDescription(transaction.description || '');
    setMerchantName(transaction.merchantName || '');
    setType(transaction.type);
    setStatus(transaction.status || 'pending');
    setNotes(transaction.notes || '');
    setOriginalValues(null); // Reset original values when transaction changes
    justSavedRef.current = false; // Reset save flag when transaction changes
  }, [transaction.id]); // Only when transaction ID changes

  // Update state when transaction prop changes (after successful save/refetch)
  // CRITICAL: Only sync when accordion is closed AND we haven't just saved
  // This prevents overwriting user edits while editing
  useEffect(() => {
    // NEVER sync if accordion is open (user is actively editing)
    if (isExpanded) {
      return;
    }
    
    // Skip syncing if we just saved (to prevent overwriting saved values with stale data)
    if (justSavedRef.current) {
      // Check if transaction prop matches what we saved
      // If it matches, we can safely sync and clear the flag
      if (lastSavedValuesRef.current) {
        const saved = lastSavedValuesRef.current;
        const matches =
          (transaction.description || '') === saved.description &&
          (transaction.merchantName || '') === saved.merchantName &&
          transaction.type === saved.type &&
          (transaction.status || 'pending') === saved.status &&
          (transaction.notes || '') === saved.notes;
        
        if (matches) {
          // Transaction prop matches what we saved, safe to clear the flag
          justSavedRef.current = false;
          lastSavedValuesRef.current = null;
          // Don't sync because state already has the correct values
          return;
        }
      }
      
      // Transaction prop doesn't match what we saved yet, wait a bit longer
      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Reset the flag after a delay to allow React Query to refetch with fresh data
      saveTimeoutRef.current = setTimeout(() => {
        justSavedRef.current = false;
        lastSavedValuesRef.current = null;
        saveTimeoutRef.current = null;
      }, 2000); // Longer delay to ensure we skip all potential stale updates
      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }
      };
    }
    
    // Only sync if values are actually different to avoid unnecessary updates
    // This ensures we don't overwrite state unnecessarily
    if (description !== (transaction.description || '')) {
      setDescription(transaction.description || '');
    }
    if (merchantName !== (transaction.merchantName || '')) {
      setMerchantName(transaction.merchantName || '');
    }
    if (type !== transaction.type) {
      setType(transaction.type);
    }
    if (status !== (transaction.status || 'pending')) {
      setStatus(transaction.status || 'pending');
    }
    if (notes !== (transaction.notes || '')) {
      setNotes(transaction.notes || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    transaction.description,
    transaction.merchantName,
    transaction.type,
    transaction.status,
    transaction.notes,
    isExpanded,
  ]);

  // Store original values when accordion opens
  useEffect(() => {
    if (isExpanded && !originalValues) {
      setOriginalValues({
        description: transaction.description || '',
        merchantName: transaction.merchantName || '',
        type: transaction.type,
        status: transaction.status || 'pending',
        notes: transaction.notes || '',
      });
    } else if (!isExpanded && originalValues) {
      // Only reset original values when accordion closes, don't reset state
      // State will be synced by the transaction prop update effect above
      setOriginalValues(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  // Update tags when transaction changes
  useEffect(() => {
    if (transaction.tags) {
      setTags(transaction.tags);
    }
  }, [transaction.tags]);

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const handleMerchantNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMerchantName(e.target.value);
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as 'debit' | 'credit' | 'transfer';
    setType(newType);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value as 'completed' | 'pending' | 'needs_review';
    setStatus(newStatus);
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(e.target.value);
  };

  const handleSave = async () => {
    try {
      setSaveStatus('saving');
      const updates: any = {};
      
      // Compare against original values if available, otherwise use current transaction prop
      const compareTo = originalValues || {
        description: transaction.description || '',
        merchantName: transaction.merchantName || '',
        type: transaction.type,
        status: transaction.status || 'pending',
        notes: transaction.notes || '',
      };
      
      // Only include fields that have changed - use strict comparison
      if (String(description).trim() !== String(compareTo.description).trim()) {
        updates.description = description || '';
      }
      if (String(merchantName || '').trim() !== String(compareTo.merchantName || '').trim()) {
        updates.merchantName = merchantName || null;
      }
      // Compare type values strictly
      if (String(type) !== String(compareTo.type)) {
        updates.type = type;
      }
      // Compare status values strictly
      if (String(status) !== String(compareTo.status)) {
        updates.status = status;
      }
      if (String(notes || '').trim() !== String(compareTo.notes || '').trim()) {
        updates.notes = notes || null;
      }

      // Debug logging
      console.log('Save clicked - Current state:', { description, merchantName, type, status, notes });
      console.log('Comparing against:', compareTo);
      console.log('Updates to save:', updates);
      console.log('Type comparison:', { current: type, compareTo: compareTo.type, match: String(type) === String(compareTo.type) });
      console.log('Status comparison:', { current: status, compareTo: compareTo.status, match: String(status) === String(compareTo.status) });

      // Only save if there are changes
      if (Object.keys(updates).length > 0) {
        console.log('Saving updates:', updates);
        const result = await updateTransactionMutation.mutateAsync({
          transactionId: transaction.id,
          updates
        });
        
        console.log('Save successful, result:', result);
        
        // CRITICAL: Use CURRENT state values (what user edited) as the saved values
        // The mutation result might not have all fields, so we use what the user actually edited
        // This ensures the state matches what was saved
        const savedValues = {
          description: description || '',
          merchantName: merchantName || '',
          type: type,
          status: status,
          notes: notes || '',
        };
        
        // Update original values to match what was saved (current state)
        // This ensures future comparisons are against the saved values
        setOriginalValues(savedValues);
        
        // Track the last saved values to compare against transaction prop
        lastSavedValuesRef.current = savedValues;
        
        // State is already correct (user's current edits), so we don't need to update it
        // The values in state are what was saved, so we keep them as-is
        
        // Mark that we just saved to prevent syncing with stale transaction prop
        // This prevents the useEffect from overwriting our saved values when React Query refetches
        justSavedRef.current = true;
        
        // Manually invalidate and refetch transaction lists to ensure UI updates
        // Do this after setting the ref so we don't sync with stale data
        await queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
        
        // Set saved status and reset after 3 seconds
        setSaveStatus('saved');
        setTimeout(() => {
          setSaveStatus('idle');
        }, 3000);
      } else {
        console.log('No changes detected, skipping save');
        setSaveStatus('idle');
      }
    } catch (error) {
      console.error('Failed to save transaction:', error);
      setSaveStatus('error');
      // Revert state on error
      if (originalValues) {
        setDescription(originalValues.description);
        setMerchantName(originalValues.merchantName);
        setType(originalValues.type);
        setStatus(originalValues.status);
        setNotes(originalValues.notes);
      } else {
        setDescription(transaction.description || '');
        setMerchantName(transaction.merchantName || '');
        setType(transaction.type);
        setStatus(transaction.status || 'pending');
        setNotes(transaction.notes || '');
      }
      // Reset error status after 3 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    try {
      const userId = await getCurrentUserId();
      if (!userId) return;
      
      const result = await transactionTagsService.removeTagFromTransaction(transaction.id, userId, tagId);
      if (result.success) {
        setTags(tags.filter(tag => tag.id !== tagId));
      }
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleSearchDescription = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation(); // Prevent row click events
    const searchQuery = encodeURIComponent(displayMerchant);
    const searchUrl = `https://www.google.com/search?q=${searchQuery}`;
    window.open(searchUrl, '_blank', 'noopener,noreferrer');
  };

  const accentColor = getAccentColor(category, transaction.type);
  const pillBackground = hexToRgba(category?.color ?? accentColor, 0.16);
  // Use transaction type for icon background instead of category color for consistency
  const iconBackground = getTypeIconBackground(transaction.type);
  // @ts-expect-error - Intentionally unused, kept for future use
  const _CategoryIconComponent = category
    ? getCategoryIconComponent({
        icon: category.icon,
        slug: category.slug,
        name: category.name,
        type: category.type,
      })
    : null;

  const displayMerchant = transaction.merchantName || transaction.description || '—';
  const formattedDate = formatDate(transaction.date);
  const accountName = account?.name;

  // Mobile/Tablet Card View
  const CardView = () => (
    <div
      className={`lg:hidden border-b border-gray-200 dark:border-gray-700 ${
        isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-white dark:bg-gray-800'
      }`}
    >
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        onClick={handleToggleExpand}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleExpand();
          }
        }}
        aria-label={isExpanded ? 'Collapse transaction details' : 'Expand transaction details'}
        aria-expanded={isExpanded}
      >
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <div className="pt-1" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onSelect(e.target.checked);
              }}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              aria-label={`Select transaction ${transaction.description}`}
            />
          </div>

          {/* Icon */}
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl flex-shrink-0"
            style={{ backgroundColor: getTypeIconBackground(transaction.type) }}
          >
            {getTypeIcon(transaction.type)}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {displayMerchant}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>{formattedDate}</span>
                  {accountName && (
                    <>
                      <span className="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
                      <span className="truncate">{accountName}</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  {category ? (
                    <span
                      className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium"
                      style={{ backgroundColor: pillBackground, color: accentColor }}
                    >
                      {category.name}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      Uncategorised
                    </span>
                  )}
                </div>
              </div>

              {/* Amount and Expand Button */}
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                <span
                  className={`text-sm font-semibold ${
                    transaction.type === 'debit'
                      ? 'text-gray-900 dark:text-gray-100'
                      : transaction.type === 'credit'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-blue-600 dark:text-blue-400'
                  }`}
                >
                  {formatAmount(transaction.amount, transaction.type)}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpand();
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label={isExpanded ? 'Collapse transaction details' : 'Expand transaction details'}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Indicators Row */}
            <div className="flex items-center gap-2 mt-2">
              {tags.length > 0 && (
                <TagIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400" aria-label="Tagged transaction" />
              )}
              {transaction.type === 'transfer' && (
                <Scissors className="h-4 w-4 text-purple-600 dark:text-purple-400" aria-label="Split transaction" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Details for Mobile/Tablet */}
      {isExpanded && (
        <div className="px-4 pb-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
          <div className="pt-4 space-y-4">
            {/* Description Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <input
                type="text"
                value={description}
                onChange={handleDescriptionChange}
                placeholder="Enter description..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Original Description Section (Read-only) */}
            {transaction.originalDescription && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Original Description
                </label>
                <input
                  type="text"
                  value={transaction.originalDescription}
                  readOnly
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                />
              </div>
            )}

            {/* Merchant Name Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Merchant Name
              </label>
              <input
                type="text"
                value={merchantName}
                onChange={handleMerchantNameChange}
                placeholder="Enter merchant name..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Type and Status Section */}
            <div className="grid grid-cols-2 gap-4">
              {/* Type Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type
                </label>
                <div className="relative">
                  <select
                    value={type}
                    onChange={handleTypeChange}
                    className="w-full px-4 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                  >
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
              </div>

              {/* Status Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <div className="relative">
                  <select
                    value={status}
                    onChange={handleStatusChange}
                    className="w-full px-4 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                  >
                    <option value="pending">Uncategorised</option>
                    <option value="needs_review">Needs Review</option>
                    <option value="completed">Categorised</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notes
              </label>
              <input
                type="text"
                value={notes}
                onChange={handleNotesChange}
                placeholder="Add notes..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Tags Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {loadingTags ? (
                  <span className="text-sm text-gray-500 dark:text-gray-400">Loading tags...</span>
                ) : tags.length > 0 ? (
                  tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: tag.color ? `${tag.color}33` : '#3B82F633',
                        color: tag.color || '#3B82F6'
                      }}
                    >
                      <span>#{tag.name}</span>
                      <button
                        onClick={() => handleRemoveTag(tag.id)}
                        className="ml-1 hover:opacity-70 focus:outline-none"
                        aria-label={`Remove tag ${tag.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400">No tags</span>
                )}
                <button
                  onClick={onTag}
                  className="inline-flex items-center px-3 py-1 border border-dashed border-gray-300 dark:border-gray-600 rounded-full text-xs font-medium text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  + Add Tag
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSave}
                  disabled={updateTransactionMutation.isPending || saveStatus === 'saving'}
                  className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    saveStatus === 'saved'
                      ? 'bg-gray-400 text-gray-700 dark:bg-gray-600 dark:text-gray-300 cursor-default'
                      : saveStatus === 'saving'
                      ? 'bg-blue-500 text-white cursor-wait'
                      : saveStatus === 'error'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                  } disabled:bg-gray-400 disabled:cursor-not-allowed`}
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : saveStatus === 'saved' ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Saved</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </>
                  )}
                </button>
              <button
                onClick={onCategorise}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <TagIcon className="h-4 w-4" />
                <span>Categorise</span>
              </button>
              <button
                onClick={onSplit}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              >
                <Scissors className="h-4 w-4" />
                <span>Split</span>
              </button>
              <button
                onClick={onDelete}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                <X className="h-4 w-4" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Conditionally render based on viewMode to fix DOM nesting issues
  if (viewMode === 'card') {
    return <CardView />;
  }

  return (
    <>
      {/* Desktop Table View */}
      <tr 
        className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
          isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
        }`}
        role="row"
        aria-selected={isSelected}
      >
      <td className="px-6 py-4 whitespace-nowrap">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          aria-label={`Select transaction ${transaction.description}`}
        />
      </td>
      {/* Date & Merchant */}
      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl"
            style={{ backgroundColor: iconBackground }}
          >
            {getTypeIcon(transaction.type)}
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{displayMerchant}</p>
              <button
                onClick={handleSearchDescription}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSearchDescription(e);
                  }
                }}
                className="flex items-center justify-center min-w-[44px] min-h-[44px] p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded transition-colors"
                aria-label={`Search for "${displayMerchant}" on the web`}
                title={`Search for "${displayMerchant}"`}
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{formattedDate}</span>
              {accountName && (
                <>
                  <span className="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
                  <span>{accountName}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </td>
      {/* Category */}
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        {category ? (
          <span
            className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium"
            style={{ backgroundColor: pillBackground, color: accentColor }}
          >
            <span>{category.name}</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            Uncategorised
          </span>
        )}
      </td>
      {/* Amount */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`text-sm font-medium ${
          transaction.type === 'debit'
            ? 'text-gray-900 dark:text-gray-100'
            : transaction.type === 'credit'
            ? 'text-green-600 dark:text-green-400'
            : 'text-blue-600 dark:text-blue-400'
        }`}>
          {formatAmount(transaction.amount, transaction.type)}
        </span>
      </td>
      {/* Indicators */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-2">
          {/* Tag indicator */}
          {tags.length > 0 && (
            <TagIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400" aria-label="Tagged transaction" />
          )}
          {/* Split indicator - show if transaction has splits (would need to check if split exists) */}
          {/* For now, we'll show it for transfers as a placeholder */}
          {transaction.type === 'transfer' && (
            <Scissors className="h-4 w-4 text-purple-600 dark:text-purple-400" aria-label="Split transaction" />
          )}
          {/* Expand/Collapse Chevron */}
          <button
            onClick={handleToggleExpand}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleExpand();
              }
            }}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded transition-transform"
            aria-label={isExpanded ? 'Collapse transaction details' : 'Expand transaction details'}
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          {/* Categorise Quick Link */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCategorise();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onCategorise();
              }
            }}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded transition-colors"
            aria-label={`Categorise transaction: ${displayMerchant}`}
            title="Categorise transaction"
          >
            <FolderTree className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </td>
      
      </tr>
      
      {/* Expanded Details Row */}
      {isExpanded && (
        <tr>
          <td colSpan={5} className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
            <div className="space-y-4">
              {/* Description Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="Enter description..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Original Description Section (Read-only) */}
              {transaction.originalDescription && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Original Description
                  </label>
                  <input
                    type="text"
                    value={transaction.originalDescription}
                    readOnly
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>
              )}

              {/* Merchant Name Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Merchant Name
                </label>
                <input
                  type="text"
                  value={merchantName}
                  onChange={handleMerchantNameChange}
                  placeholder="Enter merchant name..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Type and Status Section */}
              <div className="grid grid-cols-2 gap-4">
                {/* Type Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type
                  </label>
                  <div className="relative">
                    <select
                      value={type}
                      onChange={handleTypeChange}
                      className="w-full px-4 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                    >
                      <option value="debit">Debit</option>
                      <option value="credit">Credit</option>
                      <option value="transfer">Transfer</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  </div>
                </div>

                {/* Status Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <div className="relative">
                    <select
                      value={status}
                      onChange={handleStatusChange}
                      className="w-full px-4 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                    >
                      <option value="pending">Uncategorised</option>
                      <option value="needs_review">Needs Review</option>
                      <option value="completed">Categorised</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={handleNotesChange}
                  placeholder="Add notes..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Tags Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2">
                  {loadingTags ? (
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading tags...</span>
                  ) : tags.length > 0 ? (
                    tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: tag.color ? `${tag.color}33` : '#3B82F633',
                          color: tag.color || '#3B82F6'
                        }}
                      >
                        <span>#{tag.name}</span>
                        <button
                          onClick={() => handleRemoveTag(tag.id)}
                          className="ml-1 hover:opacity-70 focus:outline-none"
                          aria-label={`Remove tag ${tag.name}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">No tags</span>
                  )}
                  <button
                    onClick={onTag}
                    className="inline-flex items-center px-3 py-1 border border-dashed border-gray-300 dark:border-gray-600 rounded-full text-xs font-medium text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    + Add Tag
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSave}
                  disabled={updateTransactionMutation.isPending || saveStatus === 'saving'}
                  className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    saveStatus === 'saved'
                      ? 'bg-gray-400 text-gray-700 dark:bg-gray-600 dark:text-gray-300 cursor-default'
                      : saveStatus === 'saving'
                      ? 'bg-blue-500 text-white cursor-wait'
                      : saveStatus === 'error'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                  } disabled:bg-gray-400 disabled:cursor-not-allowed`}
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : saveStatus === 'saved' ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Saved</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </>
                  )}
                </button>
                <button
                  onClick={onCategorise}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  <TagIcon className="h-4 w-4" />
                  <span>Categorise</span>
                </button>
                <button
                  onClick={onSplit}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                >
                  <Scissors className="h-4 w-4" />
                  <span>Split</span>
                </button>
                <button
                  onClick={onDelete}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  <X className="h-4 w-4" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

